// src/components/RestTimerButton.jsx
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useI18n } from "../i18n/i18n";

const CIRCUMFERENCE = 2 * Math.PI * 19; // ~119.3805

export function formatTimerDigits(totalSeconds) {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

export default function RestTimerButton({ className = "" }) {
    const { t } = useI18n();
    const [isRunning, setIsRunning] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const startTimeRef = useRef(null);
    const timerRef = useRef(null);

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        startTimeRef.current = null;
        setIsRunning(false);
        setElapsedSeconds(0);
    };

    const startTimer = () => {
        startTimeRef.current = Date.now();
        setElapsedSeconds(0);
        setIsRunning(true);

        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            if (startTimeRef.current) {
                const diffSec = Math.floor((Date.now() - startTimeRef.current) / 1000);
                setElapsedSeconds(diffSec);
            }
        }, 250);
    };

    const handleToggle = () => {
        if (isRunning) {
            stopTimer();
        } else {
            startTimer();
        }
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const lap = Math.floor(elapsedSeconds / 90);
    const lapProgress = (elapsedSeconds % 90) / 90;
    const strokeOffset = CIRCUMFERENCE * (1 - lapProgress);

    let baseCircleColor = null;
    let activeStrokeColor = "#CCFF00"; // Lap 1 (0-90s): Green

    if (lap === 1) {
        // Lap 2 (90-180s): Base full green + active Cyan overlay
        baseCircleColor = "#CCFF00";
        activeStrokeColor = "#00F0FF";
    } else if (lap >= 2) {
        // Lap 3+ (180s+): Base full cyan + electric accent
        baseCircleColor = "#00F0FF";
        activeStrokeColor = "#A855F7";
    }

    const ariaLabel = isRunning
        ? `${t("log.restTimerActiveAria") || "Rest timer running"}: ${formatTimerDigits(elapsedSeconds)}`
        : t("log.restTimerIdleAria") || "Start rest timer";

    const title = isRunning
        ? t("log.restTimerActiveTitle") || "Tap to reset rest timer"
        : t("log.restTimerIdleTitle") || "Tap to start rest timer";

    return (
        <button
            type="button"
            id="restTimerButton"
            className={`restTimerButton ${isRunning ? "restTimerButton--active" : "restTimerButton--idle"} ${className}`.trim()}
            onClick={handleToggle}
            aria-label={ariaLabel}
            title={title}
        >
            {!isRunning ? (
                /* Idle state: ONLY bare washed-out stopwatch icon, zero circle, zero digits */
                <svg
                    className="restTimerIconIdle"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <circle cx="12" cy="13" r="8" />
                    <path d="M12 9v4l2 2" />
                    <path d="M10 2h4" />
                    <path d="M12 2v2" />
                </svg>
            ) : (
                /* Running state: SVG circular progress ring + centered digital readout */
                <>
                    <svg className="restTimerSvg" viewBox="0 0 44 44" aria-hidden="true">
                        <circle className="restTimerTrack" cx="22" cy="22" r="19" />
                        {baseCircleColor && (
                            <circle
                                className="restTimerBaseLap"
                                cx="22"
                                cy="22"
                                r="19"
                                stroke={baseCircleColor}
                            />
                        )}
                        <circle
                            className="restTimerActiveLap"
                            cx="22"
                            cy="22"
                            r="19"
                            stroke={activeStrokeColor}
                            style={{
                                strokeDasharray: CIRCUMFERENCE,
                                strokeDashoffset: strokeOffset,
                            }}
                        />
                    </svg>
                    <span className="restTimerDigits" aria-hidden="true">
                        {formatTimerDigits(elapsedSeconds)}
                    </span>
                </>
            )}
        </button>
    );
}

RestTimerButton.propTypes = {
    className: PropTypes.string,
};
